import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { campersApi, type DuplicateWarning } from "../../api/campers";
import { useAuth } from "../../hooks/useAuth";
import type { Camper, CamperTeam } from "../../types";

const schema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: z.string().optional(),
  team: z.enum(["", "army", "navy"]).optional(),
  excludedFromReports: z.boolean().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface CamperFormProps {
  /** Existing camper for edit mode, null/undefined for create */
  camper?: Camper | null;
  /** Called after a successful create/update */
  onSuccess: (camper: Camper) => void;
  onCancel: () => void;
}

export default function CamperForm({ camper, onSuccess, onCancel }: CamperFormProps) {
  const isEdit = !!camper;
  const navigate = useNavigate();
  const { user } = useAuth();
  // CampMinder badges are admin-debugging affordances; defense-in-depth gate
  // even though the form itself is admin/director-only.
  const showSyncBadge = user?.role !== "counselor" && user?.role !== "readonly";
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateWarning["candidates"] | null>(null);
  const [pendingValues, setPendingValues] = useState<FormData | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: camper?.firstName ?? "",
      lastName: camper?.lastName ?? "",
      dateOfBirth: camper?.dateOfBirth ?? "",
      team: (camper?.team ?? "") as "" | "army" | "navy",
      excludedFromReports: camper?.excludedFromReports ?? false,
      notes: camper?.notes ?? "",
    },
  });

  const submit = async (data: FormData, force = false) => {
    setSaving(true);
    try {
      const payload = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        dateOfBirth: data.dateOfBirth ? data.dateOfBirth : null,
        team: (data.team || null) as CamperTeam | null,
        excludedFromReports: !!data.excludedFromReports,
        notes: data.notes?.trim() ? data.notes.trim() : null,
      };

      if (isEdit && camper) {
        const updated = await campersApi.update(camper.id, payload);
        toast.success("Camper updated");
        onSuccess(updated);
        return;
      }

      const result = await campersApi.create(payload, { force });
      if ("warning" in result && result.warning === "possible_duplicate") {
        setDuplicates(result.candidates);
        setPendingValues(data);
        return;
      }
      toast.success("Camper added");
      onSuccess(result as Camper);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save camper");
    } finally {
      setSaving(false);
    }
  };

  if (duplicates) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          <p className="font-semibold">Possible duplicate{duplicates.length > 1 ? "s" : ""} found.</p>
          <p className="mt-1 text-amber-800">
            We already have a camper with this name{pendingValues?.dateOfBirth ? " and date of birth" : ""}. Use an existing record or create anyway.
          </p>
        </div>
        <ul className="space-y-2">
          {duplicates.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between border border-stone-200 rounded-lg px-3 py-2"
            >
              <div>
                <p className="text-sm font-medium text-stone-800">
                  {c.first_name} {c.last_name}
                </p>
                <p className="text-xs text-stone-500">
                  {c.date_of_birth ?? "no DOB"}
                  {showSyncBadge && c.camp_minder_person_id != null && " · CampMinder"}
                  {" · "}
                  {c.completions_count} AC{c.completions_count === 1 ? "" : "s"}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onCancel();
                  navigate(`/campers/${c.id}`);
                }}
              >
                Use existing
              </Button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={() => { setDuplicates(null); setPendingValues(null); }}>
            Back
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={saving}
            onClick={() => pendingValues && submit(pendingValues, true)}
          >
            Create anyway
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit((d) => submit(d, false))} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Input label="First name" error={errors.firstName?.message} {...register("firstName")} />
        <Input label="Last name" error={errors.lastName?.message} {...register("lastName")} />
      </div>
      <Input
        label="Date of birth (optional)"
        type="date"
        error={errors.dateOfBirth?.message}
        {...register("dateOfBirth")}
      />
      <div>
        <label htmlFor="team" className="block text-sm font-medium text-stone-700 mb-1.5">
          Team (optional)
        </label>
        <select
          id="team"
          {...register("team")}
          className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
        >
          <option value="">Unassigned</option>
          <option value="army">Army</option>
          <option value="navy">Navy</option>
        </select>
      </div>
      <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
        <input
          type="checkbox"
          {...register("excludedFromReports")}
          className="mt-0.5 rounded border-stone-300 text-forest-600 focus:ring-forest-500"
        />
        <span>
          <span className="font-medium">Exclude from reports</span>
          <span className="block text-xs text-stone-500">
            Hides this camper from all generated and printed reports without archiving the record.
          </span>
        </span>
      </label>
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-stone-700 mb-1.5">
          Notes (optional)
        </label>
        <textarea
          id="notes"
          rows={4}
          {...register("notes")}
          className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
          placeholder="Anything worth remembering about this camper…"
        />
      </div>
      {isEdit && showSyncBadge && camper?.campMinderPersonId != null && (
        <div className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
          CampMinder ID: <span className="font-mono">{camper.campMinderPersonId}</span> (read-only — managed by sync)
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" loading={saving}>
          {isEdit ? "Save changes" : "Add camper"}
        </Button>
      </div>
    </form>
  );
}
